import { createSlice, current } from "@reduxjs/toolkit";
import { api } from "../../api/httpclient";
import { addNotification } from "./notificationsslice";
import { setCurrentUser } from "./currentuserslice";
import { notificationDurationMiddle } from "../../lib/utils";
import {
  canCreateDataset,
  canEditDataset,
  canDeleteDataset,
  canViewAnyDataset,
} from "../../routing/authorizationhandlers";

const initialState = {
  pageLoaded: false,
  pageDisposed: true,
  processing: false,
  processingText: "",
  filter: "",
  showSystemEntities: true,
  items: [],
  canView: false,
  canCreate: false,
  canEdit: false,
  canDelete: false,
  panes: [
    {
      // size: "30%",
      size: "25%",
      min: "25%",
      max: "25%",
      collapsible: false,
    },
    {
      //min: "700px",
      //size:"60%",
      min: "75%",
      max: "75%",
      collapsible: false,
    },
  ],
};

const datasetsLocatorPageSlice = createSlice({
  name: "datasetsLocatorPage",
  initialState: initialState,
  reducers: {
    setInitialState: (state, action) => ({ ...initialState }),

    setProcessing: (state, action) => {
      //payload: {processing=true, processingText=false}, when processing is false, processingText is ""
      const { processing = true, processingText = "" } = action.payload;

      return {
        ...state,
        processing: processing,
        processingText: processing == false ? "" : processingText,
      };
    },

    setPanes: (state, action) => ({ ...state, panes: { ...action.payload } }),

    setPageValue: (state, action) => ({
      ...state,
      [action.payload.id]: action.payload.value,
    }),

    pageLoaded: (state, action) => {
      //payload: {reports: , currentUser: ,... }
      const pageState = current(state);

      if (pageState.pageDisposed) return state;

      return {
        ...state,
        pageLoaded: true,
        processing: false,
        processingText: "",
        items: action.payload.datasets,
        canView: canViewAnyDataset(action.payload.currentUser),
        canCreate: canCreateDataset(action.payload.currentUser),
        canEdit: canEditDataset(action.payload.currentUser),
        canDelete: canDeleteDataset(action.payload.currentUser),
        pageDisposed: false,
      };
    },

    entityDeleted: (state, action) => {
      //payload: the entity referenceKey
      let pageState = current(state); 
      if (pageState.pageDisposed) return state;
      let entityReferenceKey = action.payload;
      let newItems = pageState.items.filter((e) => e.Id != entityReferenceKey);

      return {
        ...state,
        items: newItems,
      };
    },

    disposePage: (state, action) => {
      //payload: bool
      if (action.payload) return { ...initialState };
      else return { ...state, pageDisposed: false };
    },
  },
});

export default datasetsLocatorPageSlice.reducer;

export const loadPage = (currentUser, t) => async (dispatch, getState) => {
  //start with initial values
  dispatch(setInitialState());

  //undispose page
  dispatch(disposePage(false));

  //show loader
  dispatch(
    setProcessing({ processing: true, processingText: t("slices.loadingPage") })
  );

  try {
    var response = await api({
      url: "dataset/getuserdefinedwithsystemdatasetlist",
      method: "post",
      //   data: {
      //     scope: -1,
      //   },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().datasetsLocatorPage.pageDisposed) return;

    //it's ok to dispatch here
    let data = response.data.Data;

    //transform the response
    dispatch(
      pageLoaded({
        datasets: data.Items,
        currentUser: currentUser,
      })
    );

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const deleteEntity = (referenceKey, t) => async (dispatch, getState) => {
  //payload the entity referenceKey
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.deletingDataset"),
    })
  );

  try {
    var response = await api({
      url: "/dataset/deleteuserdefineddataset",
      method: "post",
      data: { Id: referenceKey },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().datasetsLocatorPage.pageDisposed) return;

    let data = response.data.Data;

    dispatch(
      addNotification({
        text: t("slices.deletedDataset"),
        type: "success",
        closable: true,
        duration: notificationDurationMiddle,
        position: "topRight",
      })
    );

    dispatch(setProcessing({ processing: false }));

    dispatch(entityDeleted(referenceKey));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const importDatasetId = (id, t) => async (dispatch, getState) => {
  //undispose page
  dispatch(disposePage(false));

  //show loader
  dispatch(
    setProcessing({ processing: true, processingText: t("slices.loadingPage") })
  );

  try {
    var response = await api({
      url: "dataset/importdatafromdbconnectionperdataset",
      method: "post",
      data: {
        DatasetId: id,
      },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().datasetsLocatorPage.pageDisposed) return;

    //it's ok to dispatch here
    let data = response.data.Data;
    if (data.ProccessStarted) {
      dispatch(
        addNotification({
          text: t("slices.startedImport"),
          type: "success",
          closable: true,
          duration: notificationDurationMiddle,
          position: "topRight",
        })
      );
    }
    dispatch(setProcessing({ processing: false }));
    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const {
  setInitialState,
  setProcessing,
  setPageValue,
  setPanes,
  pageLoaded,
  entityDeleted,
  disposePage,
} = datasetsLocatorPageSlice.actions;
